import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../../components/Atom/breadcumbs';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ShortenId from 'components/Atom/ShortenId';
import { TIMEZONES, chargeTypes } from 'utils/constant';
import { formatInTimeZone } from 'date-fns-tz';
import { getSinglePricingModel } from 'api/subscriptions';
import { Empty } from 'antd';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { ResetButton } from '../../../components/Atom/Button';
import LoadingSkeleton from '../../../components/Skeleton/index';
import { formatAmount } from 'utils/services';
import PricingSchemaDetails from './assets/pricingSchemaDetails';
import '../../Subscriptions/style.css';
import './assets/style.css';

const formatDisplayValue = (value) => {
  if (!value) return 'N/A';
  if (value === 'fixed-retainer') return 'Fixed Retainer';
  if (value === 'one-time') return 'One-time';
  return value
    ?.split('-')
    ?.map((part) => part?.charAt(0)?.toUpperCase() + part?.slice(1))
    ?.join('-');
};

export const ViewPricingModelDetails = () => {
  const [record, setRecord] = useState(null);
  const location = useLocation();
  const store = useSelector((state) => state.company.store);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchPricingModelData = async () => {
    const response = await getSinglePricingModel(store?._id, id || location?.state?.record?.id);
    return response?.data?.data || null;
  };

  const { data, refetch, isLoading, isFetching } = useQuery(`pricingModel_${id || location?.state?.record?.id}`, fetchPricingModelData, {
    enabled: false,
    manual: true,
  });

  useEffect(() => {
    refetch();
  }, [store]);

  useEffect(() => {
    if (data) setRecord(data);
  }, [data]);

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Pricing Model',
        href: '/billing-engine/pricing-model',
        data: { id: location?.state?.id, isRedirection: true, filtersData: location?.state?.filtersData, searchValue: location?.state?.searchValue },
      },
      {
        name: <ShortenId value={id || location?.state?.id || 'Not Found'} />,
      },
    ],
  };

  const time_zone = store?.settings?.timezone || TIMEZONES.PKT;
  const isComposite = record?.charge_type === chargeTypes.COMPOSITE;

  const generalInformation = [
    { label: 'Pricing Model ID', value: <ShortenId value={record?.id} /> },
    { label: 'Name', value: record?.name || 'N/A' },
    { label: 'Model Type', value: formatDisplayValue(record?.type) },
    { label: 'Charge Type', value: formatDisplayValue(record?.charge_type) },
    { label: 'Currency', value: record?.currency || 'N/A' },
    ...(record?.baseAmount !== undefined && record?.baseAmount !== null
      ? [{ label: 'Base Amount', value: `${record?.currency || ''} ${formatAmount(record?.baseAmount, record?.currency)}`.trim() }]
      : []),
    ...(isComposite ? [...(record?.combination_operator === 'OR' ? [{ label: 'Strategy', value: record?.or_strategy ? `Whichever ${formatDisplayValue(record?.or_strategy)}` : 'N/A' }] : [])] : []),
    { label: 'Creation Time', value: record?.createdAt ? formatInTimeZone(record?.createdAt, time_zone, 'MMM d, yyyy h:mm:ss a') : '-' },
    { label: 'Last Update Time', value: record?.updatedAt ? formatInTimeZone(record?.updatedAt, time_zone, 'MMM d, yyyy h:mm:ss a') : '-' },
  ];

  const components = record?.components || [];

  return (
    <>
      <div className='header-container p-4 custom-sticky'>
        <div className='payment-breadcrums'>
          <div
            className='back-button'
            onClick={() =>
              navigate('/billing-engine/pricing-model', {
                state: {
                  id: location?.state?.id,
                  isRedirection: true,
                  filtersData: location?.state?.filtersData,
                  searchValue: location?.state?.searchValue,
                },
              })
            }>
            <ArrowLeftOutlined /> <span style={{ fontWeight: 600 }}>Back</span>
          </div>
          <span style={{ color: '#8C8C8C' }}>|</span>
          <Breadcrumbs {...breadcrumbProps} />
          <ResetButton onClick={refetch} loading={isFetching} />
        </div>
        <div className='detail-heading mb-0 mt-4'>Pricing Model Detail</div>
      </div>
      <div className='subscription-details logs-details'>
        {isLoading ? (
          <LoadingSkeleton />
        ) : !record ? (
          store?.isSelected ? (
            <div className='empty-custom-style'>
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <div className='empty-custom-style'>
              <Empty description={'Select store from the drop-down menu above'} />
            </div>
          )
        ) : (
          <>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>General Information</h3>
              </div>
              <div className='detail-card'>
                {generalInformation?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2>
                    <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className='details-section'>
              <div className='heading-section'>
                <h3>Components</h3>
              </div>
              {components?.length === 0 ? (
                <div className='empty-details'>No components configured.</div>
              ) : (
                <div className='pricing-components-wrapper'>
                  {components?.map((component, index) => (
                    <React.Fragment key={`${component?.meter_id}-${index}`}>
                      <div className='pricing-component-section'>
                        <div className='breakdown-heading'>
                          <h3>{isComposite ? `Component ${index + 1}` : 'Component'}</h3>
                        </div>
                        <div className='detail-card'>
                          <div className='detail-value'>
                            <h2>Meter ID</h2>
                            <span>
                              <ShortenId value={component?.meter_id} />
                            </span>
                          </div>
                          <div className='detail-value'>
                            <h2>Pricing Type</h2>
                            <span>{formatDisplayValue(component?.pricing_type)}</span>
                          </div>
                        </div>
                        <PricingSchemaDetails pricingType={component?.pricing_type} pricingSchema={component?.pricing_schema} currency={record?.currency} />
                      </div>
                      {isComposite && index < components.length - 1 && record?.combination_operator && (
                        <div className='component-operator-divider'>
                          <span className='component-operator-badge'>{record.combination_operator}</span>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ViewPricingModelDetails;
